import classNames from 'classnames'

import { TableAlign, TableBody, TableCell, TableRow } from '../Table'
import { Checkbox } from '../checkbox'
import {
  FinancialsColumnDefinition,
  FinancialsRowDataType,
  FinancialsSubColumnDefinition,
  FinancialsSubRowDataType,
} from './types'

type FinancialsTableRowProps<
  RowDataType extends FinancialsRowDataType,
  ColumnDefinition extends FinancialsColumnDefinition<RowDataType>,
  SubColumnDefinition extends FinancialsSubRowDataType,
> = {
  data: RowDataType
  columns: ColumnDefinition[]
  subColumns?: Record<string, FinancialsSubColumnDefinition<SubColumnDefinition>[]>
  onVisibilityChange: (area: string, visibility: boolean) => void
  isFirst: boolean
  isLast: boolean
}

export default function FinancialsTableRow<
  RowDataType extends FinancialsRowDataType,
  ColumnDefinition extends FinancialsColumnDefinition<RowDataType>,
  SubColumnDefinition extends FinancialsSubRowDataType,
>({
  data,
  columns,
  subColumns,
  onVisibilityChange,
  isFirst,
  isLast,
}: FinancialsTableRowProps<RowDataType, ColumnDefinition, SubColumnDefinition>) {
  const { id, label, labelElement, labelColumnClassName, isSelected, color, subData, isLabelColumnSticky } = data

  return (
    <TableBody
      className="hoverable-section"
      onMouseDown={() => {
        onVisibilityChange(id, !isSelected)
      }}
    >
      <TableRow
        className={classNames('is-top-border bg-primary', {
          'is-top-radius': isFirst,
          'is-bottom-border is-bottom-radius': isLast && !subData?.length,
        })}
      >
        <TableCell
          hasLeading={false}
          align={TableAlign.Left}
          className={classNames(labelColumnClassName, 'whitespace-nowrap py-1')}
          isSticky={isLabelColumnSticky}
        >
          <Checkbox
            readOnly
            checked={isSelected}
            label={labelElement ?? <span className="overflow-hidden text-ellipsis whitespace-nowrap">{label}</span>}
            className="cursor-pointer"
            color={color}
            size="none"
          />
        </TableCell>

        {columns.map((column) => (
          <TableCell
            key={column.id}
            align={column.align}
            hasLeading={false}
            className={classNames(column.className, column.getColumnClassName?.(data), 'px-3.5 py-1')}
          >
            {column.renderColumn(data, column.id)}
          </TableCell>
        ))}
      </TableRow>
      {subData?.map((item, index, array) => (
        <TableRow
          key={item.id}
          className={classNames({
            'is-bottom-border is-bottom-radius': index === array.length - 1 && isLast,
          })}
        >
          <TableCell hasLeading={false} align={TableAlign.Left} className="pl-7.5 text-muted-2">
            {item.label}
          </TableCell>
          {subColumns?.[item.id].map((column) => (
            <TableCell
              key={column.id}
              align={column.align}
              hasLeading={false}
              className={classNames(column.className, 'px-3.5 py-1')}
            >
              {column.renderColumn(item as SubColumnDefinition, column.id)}
            </TableCell>
          ))}
        </TableRow>
      ))}
    </TableBody>
  )
}
